import React, {forwardRef, useCallback} from 'react';
import style from './style.module.scss';
import {Gallery} from 'components/ProductSnippet/components/Gallery';
import {Labels} from 'components/ProductSnippet/components/Labels';
import {CartActions} from 'components/ProductSnippet/components/CartActions';
import {useProductSnippetLayout} from 'components/ProductSnippet/hooks/useProductSnippetLayout';
import {ProductSnippetHeader} from 'components/ProductSnippet/components/ProductSnippetHeader';
import {ProductSnippetContent} from 'components/ProductSnippet/components/ProductSnippetContent';
import cs from 'classnames';
import {IProductSnippet} from 'shared/types/productSnippet.types';

type TProductSnippetProps = IProductSnippet;

export const ProductSnippet = forwardRef<HTMLDivElement, IProductSnippet>(
    (props: TProductSnippetProps, externalRef) => {
        const {
            className,
            id,
            url,
            title,
            isArt,
            description,
            images,
            price,
            ratings,
            reviewsCount,
            reviewsUrl,
            isFineAndRare,
            labels,
            availability,
            wishlist,
            cart,
            properties,
            analytics: {onProductClick, onStockClick, onReviewsClick},
            hasBorder,
            stickers,
            hasCartActions,
        } = props;

        const {old, current, badges, currency, unit, percent, cashback} = price;

        const {mainRef, mainInnerRef, pixelOverflow} = useProductSnippetLayout();

        const setRefs = useCallback(
            (element: HTMLDivElement | null) => {
                if (mainRef) {
                    (mainRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
                }

                if ('function' === typeof externalRef) {
                    externalRef(element);
                } else if (externalRef) {
                    externalRef.current = element;
                }
            },
            [mainRef, externalRef],
        );

        return (
            <div ref={setRefs} className={cs(style.main, className, {[style.main_bordered]: hasBorder})}>
                <div ref={mainInnerRef} className={style.main__inner}>
                    <ProductSnippetHeader
                        id={id}
                        reviewsUrl={reviewsUrl}
                        isFineAndRare={isFineAndRare}
                        reviewsCount={reviewsCount}
                        ratings={ratings}
                        isArt={isArt}
                        onReviewsClick={onReviewsClick}
                        stickers={stickers}
                        wishlist={wishlist}
                    />
                    <Gallery
                        url={url}
                        className={style.galleryContainer}
                        imageList={images}
                        onProductClick={onProductClick}
                    />
                    <Labels labels={labels} id={id} onStockClick={onStockClick} />
                    <ProductSnippetContent
                        title={title}
                        description={description}
                        badges={badges}
                        pixelOverflow={pixelOverflow}
                        percent={percent}
                        cashback={cashback}
                        old={old}
                        current={current}
                        currency={currency}
                        unit={unit}
                        onProductClick={onProductClick}
                        properties={properties}
                        url={url}
                    />
                    {hasCartActions && <CartActions availability={availability} cart={cart} url={url} />}
                </div>
            </div>
        );
    },
);
